import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Typography, CircularProgress, Box,
  TextField, IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { NotificationManager } from "react-notifications";

/**
 * Reusable dialog for simple name-list tables (FacilityIds, SamplingPointIds).
 * Props: title, accountNumber, open, onClose, loadFn, saveFn, deleteFn
 */
const SimpleNameListDialog = ({ open, onClose, title, accountNumber, loadFn, saveFn, deleteFn }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (open && accountNumber) load();
  }, [open, accountNumber]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await loadFn(accountNumber);
      setRows(res.results || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return NotificationManager.warning("Name is required.");
    try {
      setLoading(true);
      await saveFn({ accountNumber, name: newName.trim() });
      setNewName("");
      await load();
    } catch { NotificationManager.error("Failed to add item."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteFn(id);
      await load();
    } catch { NotificationManager.error("Failed to delete item."); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title} — Account {accountNumber}</DialogTitle>
      <DialogContent dividers>
        {loading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={2}><Typography color="text.secondary" variant="body2">No items defined.</Typography></TableCell></TableRow>
                  )}
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField size="small" label="Name" value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAdd()} fullWidth />
              <Button variant="contained" size="small" onClick={handleAdd} disabled={loading}>Add</Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimpleNameListDialog;
